import { globalState } from '@/components/helpers/utils'
import { formatMoney } from '@/components/helpers/currency-formatter'

const RANGE_INDICATOR = '<->'

/**
 * Fields the index may expose the price range on. Which one is populated is an
 * AMC feed/facet configuration decision, so treat all of them as "price" rather
 * than hardcoding a single field name: `price` is the field the index currently
 * range-filters on, `ss_price_range` is the registered slider facet, `ss_price`
 * is the pre-migration name.
 */
const PRICE_FILTER_FIELDS = ['price', 'ss_price_range', 'ss_price']

/**
 * Is this facet field a price range?
 * @param {string} field - Facet field name
 * @returns {boolean}
 */
const isPriceFilterField = (field) => PRICE_FILTER_FIELDS.includes(field)

/**
 * Parse the `field:low<->high` entry for a given facet out of the active filter
 * set. Lets range UI (slider thumbs, collapsed header label) reflect what is
 * actually applied, including on a reloaded or shared URL.
 * @param {string} field - Facet field name
 * @param {Set<string>|Iterable<string>} activeFilters - Active filter strings
 * @returns {{ min: number, max: number }|null} The applied range, if any
 */
const parseActiveRange = (field, activeFilters) => {
  if (!field || !activeFilters) return null

  for (const entry of activeFilters) {
    if (typeof entry !== 'string') continue

    const parts = entry.split(':')
    if (parts.length < 2 || parts[0] !== field) continue

    // Rejoin the tail: a value may legitimately contain a colon.
    const value = parts.slice(1).join(':')
    if (!value.includes(RANGE_INDICATOR)) continue

    const [min, max] = value.split(RANGE_INDICATOR).map(Number)
    if (!Number.isFinite(min) || !Number.isFinite(max)) continue

    return { min, max }
  }

  return null
}

/**
 * Format a single price value with the shop's money format. Whole numbers drop
 * the decimals. Multi-currency safe: the format always comes from the shop
 * setting, never from an API-supplied format string.
 * @param {number|string} value - Price in the shop's currency (not cents)
 * @returns {string|number} Formatted price, or the input if it isn't numeric
 */
const formatPriceValue = (value) => {
  const amount = parseFloat(value)
  if (!Number.isFinite(amount)) return value

  const moneyFormat = globalState?.shopify?.moneyFormat
  const format = amount % 1 === 0 ? moneyFormat?.replace('amount', 'amount_no_decimals') : moneyFormat

  return formatMoney(amount * 100, format ?? undefined)
}

/**
 * Format an applied price range for display, e.g. `$100 ↔ $150`.
 * @param {number|string} min - Range low
 * @param {number|string} max - Range high
 * @returns {string}
 */
const formatPriceRangeLabel = (min, max) => `${formatPriceValue(min)} ↔ ${formatPriceValue(max)}`

export {
  formatPriceRangeLabel,
  formatPriceValue,
  isPriceFilterField,
  parseActiveRange,
  PRICE_FILTER_FIELDS,
  RANGE_INDICATOR
}
